import React from "react";

import Shimmer from "@vahak/core-ui/dist/components/Shimmer";

import HomepageV2Animate from "../animate/HomepageV2Animate";
import MarketplaceLoadCardV3 from "../marketplace-load-card-v3/MarketplaceLoadCardV3";
import MarketplacePostingNudgeCard from "../../marketplace-new/marketplace-posting-nudge-card/MarketplacePostingNudgeCard";
import {
    LOAD_MARKETPLACE_POSTING_NUDGE_TYPES,
    MARKETPLACE_POSTING_NUDGE_DESKTOP_INDEX,
    MarketplacePostingNudgeType
} from "../../marketplace-new/marketplace-posting-nudge-card/marketplacePostingNudge.constants";
import { LoadMarketCard } from "../../../services/load/useGetLoadMarketV2/type";
import GridStyles from "./AvailableLoadsGrid.module.scss";

const ANIMATION_STAGGER_MS = 75;

type AvailableLoadsGridProps = {
    loads: LoadMarketCard[];
    cardIdPrefix: string;
    onBidNow: (load: LoadMarketCard, rank: number) => void;
    showPostingNudge?: boolean;
    isLoading?: boolean;
    shimmerCount?: number;
};

const AvailableLoadsGrid = ({
    loads,
    cardIdPrefix,
    onBidNow,
    showPostingNudge = false,
    isLoading = false,
    shimmerCount = 5
}: AvailableLoadsGridProps) => {
    if (isLoading && !loads.length) {
        return (
            <div className={GridStyles.grid}>
                {Array.from({ length: shimmerCount }).map((_, index) => (
                    <Shimmer key={index} minHeight={285} borderRadius={14} aria-busy />
                ))}
            </div>
        );
    }

    const renderLoad = (load: LoadMarketCard, rank: number, animIndex: number) => (
        <HomepageV2Animate
            key={load.load_detail?.id || `${cardIdPrefix}-load-${rank}`}
            variant="scale-up"
            delay={animIndex * ANIMATION_STAGGER_MS}
            className={GridStyles.gridItem}
        >
            <MarketplaceLoadCardV3
                id={`${cardIdPrefix}-load-${rank}`}
                data={load}
                onBidNowClick={() => onBidNow(load, rank)}
            />
        </HomepageV2Animate>
    );

    const renderPostingNudge = (type: MarketplacePostingNudgeType, animIndex: number) => (
        <HomepageV2Animate
            key={`${cardIdPrefix}-post-${type}-nudge`}
            variant="scale-up"
            delay={animIndex * ANIMATION_STAGGER_MS}
            className={GridStyles.nudgeSlot}
        >
            <MarketplacePostingNudgeCard type={type} />
        </HomepageV2Animate>
    );

    if (!showPostingNudge) {
        return <div className={GridStyles.grid}>{loads.map((load, index) => renderLoad(load, index + 1, index))}</div>;
    }

    const nudgeAfter = Math.min(MARKETPLACE_POSTING_NUDGE_DESKTOP_INDEX, loads.length);
    const loadsBeforeNudge = loads.slice(0, nudgeAfter);
    const loadsAfterNudge = loads.slice(nudgeAfter);
    const nudgeTypeCount = LOAD_MARKETPLACE_POSTING_NUDGE_TYPES.length;

    return (
        <div className={GridStyles.grid}>
            {loadsBeforeNudge.map((load, index) => renderLoad(load, index + 1, index))}
            {LOAD_MARKETPLACE_POSTING_NUDGE_TYPES.map((type, index) => renderPostingNudge(type, nudgeAfter + index))}
            {loadsAfterNudge.map((load, index) =>
                renderLoad(load, nudgeAfter + index + 1, nudgeAfter + nudgeTypeCount + index)
            )}
        </div>
    );
};

export default AvailableLoadsGrid;
